import React, { useState, useEffect, useContext, memo, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';

// Helper.
import Helper from '../../helpers/Helper';

// Api.
import api from '../../api/Base';

// Hooks.
import { useInstanceStatus } from '../../hooks/useInstanceStatus';
import { useHostStatus } from '../../hooks/useHostStatus';

// Types.
import InstanceTarget from '../../types/instance/InstanceTarget';
import HostTarget from '../../types/host/HostTarget';
import EventType from '../../types/instance/EventType';
import { InstanceContext } from "../../context/InstanceContext";
import { HostContext } from "../../context/HostContext";
import * as dayjs from "dayjs";
import ConditionalRender from "../../helpers/ConditionalRender";

interface IActionButtonsProps {
    host?: HostTarget,
    instance?: InstanceTarget,
    removeFromList?: Function,
    eventTypes: EventType[],
    isAdmin: boolean,
    onDuplicate?: (item: any) => void,
    rowData?: any
}

const ActionButtons: React.FC<IActionButtonsProps> = ({ host, instance, eventTypes, isAdmin, onDuplicate, rowData }) => {
    // Only use the hooks that are actually needed to avoid unnecessary API calls
    const instanceStatus = useInstanceStatus(instance?.id || 0);
    const hostStatus = useHostStatus(host?.id || 0);

    const [showHostDropdown, setShowHostDropdown] = useState<boolean>(false);
    const [showInstanceDropdown, setShowInstanceDropdown] = useState<boolean>(false);

    const hostDropdownRef = useRef<HTMLDivElement>(null);
    const instanceDropdownRef = useRef<HTMLDivElement>(null);

    const hostContext = useContext(HostContext);
    const instanceContext = useContext(InstanceContext);

    // Use the appropriate status based on whether we're showing instance or host
    const status = instance ? instanceStatus : hostStatus;

    const lastContactedValue = useMemo(() => {
        if (status.updated) {
            const parsedDate = dayjs.default(status.updated);
            const statusValue = dayjs.default(parsedDate.format('YYYY-MM-DD HH:mm'), 'YYYY-MM-DD HH:mm').fromNow().replace('in a few seconds', 'A few seconds ago')
            return statusValue;
        } else {
            return '-';
        }
    }, [status.updated]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (hostDropdownRef.current && !hostDropdownRef.current.contains(event.target as Node)) {
                setShowHostDropdown(false);
            }
            if (instanceDropdownRef.current && !instanceDropdownRef.current.contains(event.target as Node)) {
                setShowInstanceDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleTargetStatus = async (monitor: boolean) => {
        // For both hosts and instances, the status is managed by the hooks
        // The loading/state changes will be reflected when the next poll happens

        let id = 0;
        let url = '';
        let toastMessage = '';
		let data: HostTarget[] | InstanceTarget[] = [];

        if (host) {
            id = host.id;
            url = 'host';

            const updatedHosts = hostContext.hosts.map((obj: HostTarget) =>
                    obj.id === id ? { ...obj, monitor, password: obj.password === '' ? null : obj.password, attempt: obj.attempt + 1, reason: 'User-initiated' } : obj
            );

            hostContext.setHosts(updatedHosts);
            toastMessage = monitor ? 'Stopping host sensor' : 'Starting host sensor';
			data = updatedHosts.filter ((obj: HostTarget) => obj.id === id);
        }

        if (instance) {
            id = instance.id;
            url = 'datasource';

            const updatedInstances = instanceContext.instances.map((obj: InstanceTarget) =>
                    obj.id === id ? { ...obj, monitor, password: obj.password === '' ? null : obj.password, attempt: obj.attempt + 1, reason: 'User-initiated' } : obj
            );

            instanceContext.setInstances(updatedInstances);

            toastMessage = monitor ? 'Stopping instance sensor...' : 'Starting instance sensor...';
			data = updatedInstances.filter ((obj: InstanceTarget) => obj.id === id);
        }

        const result = await api.put(url, data);

        if (result.status !== 200) {
            console.error('Failed to update status', result, data, host || instance, toastMessage);
            return;
        }

        if (instance) {
            Helper.createInstanceEvent(eventTypes, id, monitor ? 'Monitoring Enabled' : 'Monitoring Disabled', monitor ? 'Enabling monitoring' : 'Stopping monitoring');
        }
    };

    let buttonClassName = 'btn btn-sm btn-secondary';
    let iconClassName = 'fal fa-stop fa-fw';
    let buttonTooltip = status.state;
    let buttonTitle = 'Stop';

    if (status.reason) {
        buttonTooltip = `${status.state} - ${status.reason.toLowerCase()}`;
    }

    if (status.state === 'Stopped' || status.state === 'Stopping') {
        buttonClassName = 'btn btn-sm btn-outline-secondary';
        iconClassName = 'fal fa-play fa-fw';
        buttonTitle = 'Start';
    }

    return (
            <>
                <div className='d-flex justify-content-between'>
                    <div className='text-start last-contacted'>
                        {status.loading ? <span /> : `${lastContactedValue}    `}
                    </div>
                    <div className="d-flex gap-1 align-items-center">
                        <ConditionalRender if={isAdmin}>
                            <button
                                    type="button"
                                    className={`${buttonClassName} ${status.loading ? 'loading' : ''}`}
                                    data-tip={buttonTooltip}
                                    onClick={() => !status.loading && toggleTargetStatus(buttonTitle !== 'Stop')}
                                    // disabled={status.loading}
                            >
                                <>
                                    <i className={iconClassName}></i>
                                    <span>{buttonTitle}</span>
                                </>
                            </button>
                        </ConditionalRender>
                        {host && (
                                <>
                                    <Link to={`/admin/hosts/edit/${host.id}`} role="button" className="btn btn-sm btn-primary">
                                        <i className="fal fa-edit fa-fw"></i><span>Edit</span>
                                    </Link>
                                    <Link className='btn btn-sm btn-success' to={`/hosts/${host.id}`}>
                                        <i className="fal fa-eye fa-fw"/><span>View</span>
                                    </Link>
                                    <div className="dropdown" ref={hostDropdownRef} style={{ position: 'relative' }}>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-secondary dropdown-toggle"
                                            onClick={() => setShowHostDropdown(!showHostDropdown)}
                                            aria-expanded={showHostDropdown}
                                        >
                                            <i className="fal fa-ellipsis-h fa-fw"></i><span>Actions</span>
                                        </button>
                                        {showHostDropdown && (
                                            <ul className="dropdown-menu show" style={{ display: 'block', right: 0, left: 'auto', position: 'absolute', zIndex: 1050 }}>
                                                <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => {
                                                            onDuplicate && onDuplicate(rowData);
                                                            setShowHostDropdown(false);
                                                        }}
                                                    >
                                                        <i className="fal fa-copy fa-fw me-2"></i>Duplicate
                                                    </button>
                                                </li>
                                                <li>
                                                    <Link
                                                        className="dropdown-item text-danger"
                                                        to={`/admin/hosts/delete/${host.id}`}
                                                        onClick={() => setShowHostDropdown(false)}
                                                    >
                                                        <i className="fal fa-trash-alt fa-fw me-2"></i>Delete
                                                    </Link>
                                                </li>
                                            </ul>
                                        )}
                                    </div>
                                </>
                        )}
                        {instance && (
                                <>
                                    <Link to={`/admin/instances/edit/${instance.id}`} role="button" className="btn btn-sm btn-primary">
                                        <i className="fal fa-edit fa-fw"></i><span>Edit</span>
                                    </Link>
                                    <Link className='btn btn-sm btn-success' to={`/instances/${instance.id}/activity`}>
                                        <i className="fal fa-eye fa-fw" /><span>View</span>
                                    </Link>
                                    <div className="dropdown" ref={instanceDropdownRef} style={{ position: 'relative' }}>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-secondary dropdown-toggle"
                                            onClick={() => setShowInstanceDropdown(!showInstanceDropdown)}
                                            aria-expanded={showInstanceDropdown}
                                        >
                                            <i className="fal fa-ellipsis-h fa-fw"></i><span>Actions</span>
                                        </button>
                                        {showInstanceDropdown && (
                                            <ul className="dropdown-menu show" style={{ display: 'block', right: 0, left: 'auto', position: 'absolute', zIndex: 1050 }}>
                                                <li>
                                                    <button
                                                        className="dropdown-item"
                                                        onClick={() => {
                                                            onDuplicate && onDuplicate(rowData);
                                                            setShowInstanceDropdown(false);
                                                        }}
                                                    >
                                                        <i className="fal fa-copy fa-fw me-2"></i>Duplicate
                                                    </button>
                                                </li>
                                                <li>
                                                    <Link
                                                        className="dropdown-item text-danger"
                                                        to={`/admin/instances/delete/${instance.id}`}
                                                        onClick={() => setShowInstanceDropdown(false)}
                                                    >
                                                        <i className="fal fa-trash-alt fa-fw me-2"></i>Delete
                                                    </Link>
                                                </li>
                                            </ul>
                                        )}
                                    </div>
                                </>
                        )}
                        <ReactTooltip className="tooltip" effect="solid" />
                    </div>
                </div>
            </>
    );
};

export default memo(ActionButtons);
